import React, { useEffect, useState } from 'react';
import { InsurancePlan, PlanService, MedicalGroup, Provider, Network } from '../../utils/types';
import { Button, Grid, Link } from '@mui/material';
import { buttonStyles, LinkStyles, PerlmanGreen } from '../../theme';
import { StyledCrumbs, BreadCrumbsContainer, InNetworkContainer, AppointmentButtonContainer } from './styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleExclamation } from '@fortawesome/free-solid-svg-icons';
import LearnMorePCPModal from '../Modals/LearnMorePCPModal';
import StartOverButton from '../StartOverButton';
import { EventAction, trackMixpanelEvent } from '../../mixpanelAnalytics';

type CrumbProps = {
    step:string;
    name: string | undefined;
}

const Crumb = ({ step, name }:CrumbProps):JSX.Element => {
    const [ open, setOpen ] = useState(false);
    if (!name) {
        return (<div/>);
    }
    return (
        <StyledCrumbs className='fadeIn'>
            <Grid container>
                <Grid xs={6} md={5}>
                    <Grid item><h2>{step}:</h2></Grid>
                </Grid>
                <Grid xs={6} md={7}>
                    <Grid item><h4>{name}</h4></Grid>
                </Grid>
            </Grid>
            {
                step === 'PCP' ?
                    <>
                        <p>
                            <FontAwesomeIcon size='lg' icon={faCircleExclamation} style={{ color: PerlmanGreen, margin: '0px 10px' }}/>
                            As a reminder, you can always see any provider at Perlman Clinic.
                            <span style={LinkStyles} onClick={() => { setOpen(true); }}> Learn More</span>
                        </p>
                        <LearnMorePCPModal handleClose={() => { setOpen(false); }} open={open}/>
                    </>
                    : null
            }
        </StyledCrumbs>
    );
};
const LinkElement = ({ url }:{ url:string }):JSX.Element => {
    return (<Link href={url} style={{ color: '#FFF', textDecorationColor: '#FFF' }} target="new">{url}</Link>);
};

type LinkTextProps = {
    s :string;
}
const LinkedText = ({ s }:LinkTextProps):JSX.Element => {
    const URL_REGEX = /http(s)?:\/\/([a-zA-z]|\/|\d|\.|-|\?|&|=)+/;
    if (!URL_REGEX.test(s)) {
        return (<span>{s}</span>);
    }
    const parsed = s.split(' ');
    return (
        <span>
            {
                parsed.map((p, i) => {
                    if (p.match(URL_REGEX)) {
                        return (<span key={i}><LinkElement url={p}/> </span>);
                    }
                    return (<span key={i}>{p} </span>);
                })
            }
        </span>
    );
};

type ExceptionProps = {
    plan: InsurancePlan | null | undefined;
    group: MedicalGroup | null | undefined;
    network: string | null | undefined;
}

const Exceptions = ({ plan, group, network }:ExceptionProps): JSX.Element => {
    const canShowExceptions = ():boolean => {
        return (!!group?.exceptions?.length || (!!plan?.exceptions?.length && network !== Network.HMO));
    };
    if (!canShowExceptions()) {
        return (<></>);
    }
    return (
        <div style={{ display: 'flex' }}>
            <p>*The following exceptions apply:</p>
            <ul style={{ listStyle: 'none', padding: '0px 0 0 12px' }}>
                {
                    network !== Network.HMO ?
                        plan?.exceptions?.map((e, i) => {
                            return (<li style={{}} key={i}><LinkedText s={e}/></li>);
                        })
                        : null
                }
                {
                    group?.exceptions?.map((e, i) => {
                        return (<li style={{}} key={i}><LinkedText s={e}/></li>);
                    })
                }
            </ul>
        </div>
    );
};

type Props = {
    service: PlanService | null | undefined;
    plan: InsurancePlan | null | undefined;
    network: string | null | undefined;
    group: MedicalGroup | null | undefined;
    provider: Provider | null | undefined;
    inNetwork: boolean;
    show: boolean;
    handleStartOver: () => void;
}

const BreadCrumbs = ({ service, plan, network, group, provider, inNetwork, show, handleStartOver }:Props): JSX.Element => {
    useEffect(() => {
        if (inNetwork) {
            trackMixpanelEvent(EventAction.NETWORK_STATUS, { status: 'In Network' });
        }
    }, [ inNetwork ]);
    const handleScheduleAppointment = () => {
        trackMixpanelEvent(EventAction.SCHEDULE_APPOINTMENT);
        const w = window.open('https://schedule.perlmanclinic.com/', '_blank');
        w?.focus();
    };
    return (
        <>
            <BreadCrumbsContainer className='fadeIn' sx={{ display: show ? 'block' : 'none' }}>
                <InNetworkContainer sx={{ display: inNetwork ? 'block' : 'none' }}>
                    <h1>You're in-network!</h1>
                    <Exceptions plan={plan} group={group} network={network}/>
                </InNetworkContainer>
                <div style={{ padding: '15px' }}>
                    <Crumb step='Health Plan' name={network ? `${plan?.name} ${network.toUpperCase()}` : plan?.name}/>
                    <Crumb step='Service' name={service?.name}/>
                    <Crumb step='Medical Group/IPA' name={group?.name}/>
                    <Crumb step='PCP' name={provider ? `${provider.firstName} ${provider.lastName}` : ''}/>
                </div>
            </BreadCrumbsContainer>
            <AppointmentButtonContainer style={{ display: inNetwork ? 'flex' : 'none' }}>
                <Button sx={buttonStyles} variant='contained' onClick={handleScheduleAppointment}>Schedule Appointment</Button>
                <StartOverButton handleStartOver={handleStartOver}/>
            </AppointmentButtonContainer>
        </>
    );
};

export default BreadCrumbs;
